import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  Filters,
  IconLoader,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  usePrevious,
  ButtonSecondary,
  ButtonSize,
  Popup,
  useGetList,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  countriesListColumns,
  countriesListColumnsLabels,
  countriesListFiltersSchema,
  countriesListSchema,
} from '../variables/countries';
import {
  onSortList,
  onFilterList,
  onSelectedRows,
} from '../store/slices/countries';
import {
  useLazyGetCountActiveCountriesQuery,
  useLazyGetEmployeesCountriesQuery,
  useToggleCountriesActiveMutation,
} from '../store/services/countries';
import { CountryItem } from '../types';
import formatCountriesList from '../helpers/format-countries-list';

import cn from '../styles.module.css';

const CountriesList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activatePopupVisible, setActivatePopupVisible] = useState(false);
  const [disablePopupVisible, setDisablePopupVisible] = useState(false);

  const {
    pagination,
    values: countries,
    selectedCountryIds,
    activeCount,
    disableActivateBtn,
    disableInactivateBtn,
    errorsList,
  } = useAppSelector((state) => state.settings.countries);

  const [getList, { isFetching, isLoading }] =
    useLazyGetEmployeesCountriesQuery();
  const [getCountActiveCountries] = useLazyGetCountActiveCountriesQuery();
  const [toggleCountriesActive] = useToggleCountriesActiveMutation();

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(countriesListSchema);

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatCountriesList(countries as CountryItem[]);

    return data;
  }, [countries, sort, filters]);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(rows, selectedCountryIds)) {
      dispatch(onSelectedRows(rows));
    }
  };

  const onSort = (name: string) => {
    onSortChange(name);

    dispatch(
      onSortList({ name, direction: sort.sortDir === 'asc' ? 'desc' : 'asc' }),
    );
  };

  const onFilter = (filterValues: Filters) => {
    onFilterChange(filterValues);

    dispatch(onFilterList(filterValues));
  };

  const getCountActiveCountriesAction = useCallback(() => {
    getCountActiveCountries();
  }, []);

  const { getListAction, setPage, errorList } = useGetList<CountryItem>({
    getList,
    onFilterChange,
    onSortChange,
    schema: countriesListSchema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: countriesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !countries
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, countries]);

  useEffect(() => {
    getCountActiveCountriesAction();
  }, [getCountActiveCountriesAction]);

  const onActionCountries = (value: boolean) => {
    if (selectedCountryIds) {
      toggleCountriesActive({ active: value, ids: selectedCountryIds });
      setActivatePopupVisible(false);
      setDisablePopupVisible(false);

      setTimeout(() => {
        getListAction({
          filters,
          sort,
        });
        getCountActiveCountriesAction();
      });
    }
  };

  useEffect(() => {
    let title = 'Countries';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.tabLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={countriesListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        onRowsSelect={onRowsSelect}
        filters={filters}
        sort={sort}
        columnsLabels={countriesListColumnsLabels}
        onFilterChange={(filterValues) => onFilter(filterValues)}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        error={errorsList || errorList}
        take="30"
        setPage={(data) => setPage({ ...data, filters })}
        pagination={pagination}
        clickable
      />
    );
  }

  return (
    <div className={cn.currenciesWrapper}>
      <div className={cn.settingsTitle}>Countries</div>
      <div className={cn.countriesInfo}>
        <div className={cn.infoWrapper}>
          <div className={cn.infoTitle}>Number of available countries:</div>
          {activeCount && (
            <div
              className={cn.infoValue}
            >{`${activeCount} active countries`}</div>
          )}
        </div>
      </div>

      <div className={cn.buttons}>
        <ButtonSecondary
          text="Activate"
          size={ButtonSize.SMALL}
          disabled={disableActivateBtn}
          onClick={() => setActivatePopupVisible(true)}
        />
        <ButtonSecondary
          text="Disable"
          size={ButtonSize.SMALL}
          disabled={disableInactivateBtn}
          onClick={() => setDisablePopupVisible(true)}
        />
      </div>

      <div className={cn.contentWrapper} ref={scrollRef}>
        {content}
      </div>

      <Popup
        title="You activate countries"
        okButtonText="Yes, activate"
        description="The countries will become available for various settings and calculations. Activate?"
        cancelButtonText="Cancel"
        onCancelClick={() => setActivatePopupVisible(false)}
        onOkClick={() => onActionCountries(true)}
        isVisible={activatePopupVisible}
      />
      <Popup
        title="You disable off the currency"
        description="The countries will not become available for various settings and calculations. Disable?"
        okButtonText="Yes, disable"
        cancelButtonText="Cancel"
        onCancelClick={() => setDisablePopupVisible(false)}
        onOkClick={() => onActionCountries(false)}
        isVisible={disablePopupVisible}
      />
      <Outlet />
    </div>
  );
};

export default CountriesList;
